/**
 * Students sign up from the app (owner decision 2026-11-03): their number is checked by a WhatsApp code,
 * the account opens at once without a circle, and they choose their program. The administration then
 * places them in a circle, which approves the plan they chose.
 */
import { z } from "zod";
import { Gender, phone } from "./fields.js";
import { AmountUnit, Weekday } from "./policies.js";
const surah = z.number().int().min(1).max(114);
const ayah = z.number().int().min(1).max(286);
const point = z.object({ surah, ayah });
/** What the student wants: memorization alone, with the cumulative review, with the far review too, or review only. */
export const SIGNUP_KINDS = ["memorize", "memorize_review", "memorize_review_far", "review"];
export const SignupKind = z.enum(SIGNUP_KINDS);
/** The time of day the student prefers (a wish, not the circle's time). */
export const STUDY_PERIODS = ["fajr", "dhuhr", "asr", "maghrib", "isha"];
export const StudyPeriod = z.enum(STUDY_PERIODS);
const amount = z.object({ amount: z.number().positive().max(60), unit: AmountUnit });
export const RegistrationProgram = z
    .object({
    kind: SignupKind,
    /** Memorization: the first ayah to memorize, the direction and the daily amount. */
    start: point.optional(),
    direction: z.enum(["backward", "forward"]).optional(),
    daily: amount.optional(),
    /** The far review (or, for review only, the review): the part memorized before, and the daily amount. */
    review: z.object({ from: point, to: point, daily: amount }).optional(),
    studyDays: z.array(Weekday).min(1).max(7),
    period: StudyPeriod,
})
    .superRefine((p, ctx) => {
    const memorizes = p.kind !== "review";
    if (memorizes && (!p.start || !p.direction || !p.daily))
        ctx.addIssue({ code: "custom", message: "حدّد بداية الحفظ واتجاهه ومقداره اليومي", path: ["start"] });
    if ((p.kind === "memorize_review_far" || p.kind === "review") && !p.review)
        ctx.addIssue({ code: "custom", message: "حدّد مقطع المراجعة ومقدارها اليومي", path: ["review"] });
});
export const StudentSignupBody = z.object({
    phone,
    /** The WhatsApp code sent to the number (purpose "register"). */
    code: z
        .string()
        .trim()
        .regex(/^\d{4,8}$/, "اكتب الرمز الذي وصلك"),
    name: z.string().trim().min(2, "اكتب الاسم الكامل").max(120),
    gender: Gender,
    birthYear: z.number().int().min(1930).max(2030).optional(),
    /** A new account's password; an existing account (signing up with its own number) keeps its own. */
    password: z.string().min(8, "كلمة المرور ٨ أحرف على الأقل").max(128).optional(),
    program: RegistrationProgram,
});
export const StudentSignupResponse = z.object({ token: z.string(), expiresAt: z.string() });
export const RegistrationStatus = z.enum(["pending", "placed", "cancelled"]);
export const Registration = z.object({
    id: z.uuid(),
    status: RegistrationStatus,
    student: z.object({
        id: z.uuid(),
        name: z.string(),
        phone: z.string().nullable(),
        gender: Gender.nullable(),
        birthYear: z.number().nullable(),
    }),
    program: RegistrationProgram,
    /** The program in words, line by line («حفظ: من سورة … وجه يوميًا»). */
    summary: z.array(z.string()),
    createdAt: z.string(),
    placedAt: z.string().nullable(),
    circle: z.object({ id: z.uuid(), name: z.string() }).nullable(),
});
export const RegistrationsResponse = z.object({ registrations: z.array(Registration) });
export const MyRegistrationResponse = z.object({ registration: Registration.nullable() });
/** Places the student in a circle: enrolls them and approves the plan they chose, from `from`. */
export const PlaceRegistrationBody = z.object({
    circleId: z.uuid(),
    from: z.iso.date(),
    /** Only for a student whose account has no gender yet (an administrator who signed up as a student). */
    gender: Gender.optional(),
});
export const PlaceRegistrationResponse = z.object({ enrollmentId: z.uuid(), planId: z.uuid() });
export const PublicRegistrationResponse = z.object({ reviewMultiple: z.number() });
/**
 * Accounts whose roles were all withdrawn (owner request 2026-11-03): they no longer show among users, so
 * the administration finds them here to give them a role again or delete them for good.
 */
export const FormerMember = z.object({
    id: z.uuid(),
    name: z.string(),
    phone: z.string().nullable(),
    gender: Gender.nullable(),
    lastRole: z.string(),
    withdrawnAt: z.string(),
});
export const FormerMembersResponse = z.object({ members: z.array(FormerMember) });
/** Deletes an account for good (the same removal as a completed deletion request). */
export const DeleteAccountBody = z.object({ organizationId: z.uuid() });
//# sourceMappingURL=registrations.js.map